type RequestBody = Record<string, string | number | boolean | undefined>;

interface BaseOptions {
  keepalive?: boolean;
  parameters?: Record<string, string | number | boolean>;
  url: string;
  useApiKeys?: boolean;
  withCredentials?: boolean;
}

interface GetOptions extends BaseOptions {
  method?: "GET";
}

interface PostOptions extends BaseOptions {
  body?: RequestBody;
  method?: "POST";
}

interface DeleteOptions extends BaseOptions {
  method?: "DELETE";
}

interface PutOptions extends BaseOptions {
  body?: RequestBody;
  method?: "PUT";
}

interface RequestOptions extends BaseOptions {
  body?: RequestBody;
  method: "GET" | "POST" | "DELETE" | "PUT";
}

export function put<T>(options: PutOptions): Promise<T> {
  return request<T>({ ...options, method: "PUT" }) as Promise<T>;
}

export function getJson<T>(options: GetOptions): Promise<T> {
  return request<T>({ ...options, method: "GET" }) as Promise<T>;
}

export function post<T>(options: PostOptions): Promise<T> {
  return request<T>({ ...options, method: "POST" }) as Promise<T>;
}

export function deleteJson<T>(options: DeleteOptions): Promise<T> {
  return request<T>({ ...options, method: "DELETE" }) as Promise<T>;
}

export async function request<T>({
  body,
  keepalive,
  method,
  parameters = {},
  url,
  useApiKeys = true,
  withCredentials = false
}: RequestOptions): Promise<T | void> {
  const searchParams = new URLSearchParams(useApiKeys ? __API_KEYS : "");

  Object.entries(parameters).forEach(([key, value]) => {
    searchParams.append(key, String(value));
  });

  const urlWithParams = searchParams.size > 0 ? `${url}?${searchParams}` : url;

  const response = await fetch(urlWithParams, {
    method,
    credentials: withCredentials ? "include" : "same-origin",
    headers: body != null ? { "Content-Type": "application/json" } : undefined,
    body: body != null ? JSON.stringify(body) : undefined,
    keepalive
  });

  if (!response.ok) {
    const errorMessage = `API request failed: ${method} ${url} - Status: ${response.status} Text: ${response.statusText}`;
    console.error(errorMessage);
    throw new Error(errorMessage);
  }

  const text = await response.text();
  return text ? JSON.parse(text) : undefined;
}
